/*
 * Ollama, in front of the grammar.
 *
 * The model TRANSLATES prose into a command string. It never acts. What it
 * produces goes to /api/voice like anything typed by hand, and the server's
 * parser is still the thing that decides what is allowed — so a hallucinated
 * "C99 done" is refused exactly as a typo is, and a hallucinated verb is not
 * understood exactly as nonsense is. Standard 20a: the model goes IN FRONT of
 * the grammar, never instead of it.
 *
 * It runs in the BROWSER, against http://localhost:11434, not from the Worker:
 *
 *  - The Worker is on Cloudflare's edge and cannot reach a laptop.
 *  - Chrome treats http://localhost as a trustworthy origin, so an HTTPS page is
 *    allowed to call it without a mixed-content block. No tunnel is needed and
 *    nothing has to be exposed to the internet.
 *  - The deal text — broker names, balances, the escrow position — never leaves
 *    the machine for the translation step. That is worth more here than the
 *    model quality.
 *
 * Absent Ollama, everything still works. This is an assist on the path that
 * previously ended in a ticket, not a dependency.
 */

const ENDPOINT_KEY = 'mc.ollama.endpoint'
const MODEL_KEY = 'mc.ollama.model'
export const DEFAULT_ENDPOINT = 'http://localhost:11434'
export const DEFAULT_MODEL = 'qwen2.5:7b-instruct'

const read = (key: string, fallback: string): string => {
  try {
    return localStorage.getItem(key) || fallback
  } catch {
    // Private windows and blocked site data both throw here.
    return fallback
  }
}

export const endpoint = () => read(ENDPOINT_KEY, DEFAULT_ENDPOINT)
export const model = () => read(MODEL_KEY, DEFAULT_MODEL)

export function configure(next: { endpoint?: string; model?: string }): void {
  try {
    if (next.endpoint !== undefined) localStorage.setItem(ENDPOINT_KEY, next.endpoint)
    if (next.model !== undefined) localStorage.setItem(MODEL_KEY, next.model)
  } catch {
    /* nothing to do: the setting simply will not persist */
  }
}

export interface Status {
  up: boolean
  models: string[]
  reason?: string
}

export async function status(): Promise<Status> {
  try {
    const res = await fetch(`${endpoint()}/api/tags`, { signal: AbortSignal.timeout(2500) })
    if (!res.ok) return { up: false, models: [], reason: `Ollama answered ${res.status}` }
    const body = (await res.json()) as { models?: Array<{ name: string }> }
    return { up: true, models: (body.models ?? []).map((m) => m.name) }
  } catch {
    return { up: false, models: [], reason: 'Ollama is not answering on this machine.' }
  }
}

export interface Vocabulary {
  help: { groups: Array<{ name: string; lines: string[][] }> }
  rows: Array<{ ref: string; label: string; kind: string; context?: string; status?: string }>
}

/*
 * Tools, not a command string.
 *
 * The first build asked the model to emit a line of command syntax and replied
 * NONE to anything it could not shape — so "has the deposit gone out yet?" got
 * nothing at all, and the question became a ticket. A tool schema removes that
 * failure: the model picks a tool and fills named arguments, and the argument
 * names carry meaning the free-text prompt had to imply.
 *
 * The pattern is lifted from miDash, including the part that matters most: a
 * tool declares whether it ACTS IMMEDIATELY or needs confirming. Reading a row
 * changes nothing and happens at once. Everything that writes is shown to the
 * operator first, in the words of what it is about to do.
 */
export interface Tool {
  name: string
  description: string
  parameters: Record<string, unknown>
  /** False for anything that writes: the operator sees it before it happens. */
  immediate: boolean
}

export const TOOLS: Tool[] = [
  {
    name: 'show_row',
    description:
      'Show everything recorded about one row: its status, owner, due date, EVIDENCE (which documents or proof exist for it), next action, risk if late, and any notes. Use this for ANY question about the deal — "has the deposit gone out", "did the pdfs get uploaded", "who owns the CPA session", "what is happening with X". A question is never a ticket. Read-only.',
    parameters: { type: 'object', properties: { ref: { type: 'string', description: 'Row id, e.g. C04, G1, R14, K09' } }, required: ['ref'] },
    immediate: true,
  },
  {
    name: 'set_status',
    description:
      'Change the status of a row. Tasks take Complete, In Progress, Not Started or Blocked. Gates take Passed, Open or Failed. Risks take Closed, Open or Mitigated. Use when the person says something is done, finished, started, blocked, passed or closed.',
    parameters: {
      type: 'object',
      properties: { ref: { type: 'string' }, status: { type: 'string', description: 'One of the values listed for that kind of row' } },
      required: ['ref', 'status'],
    },
    immediate: false,
  },
  {
    name: 'set_due',
    description: 'Move a task or gate to a different due date. Dates are always YYYY-MM-DD.',
    parameters: { type: 'object', properties: { ref: { type: 'string' }, date: { type: 'string', description: 'YYYY-MM-DD' } }, required: ['ref', 'date'] },
    immediate: false,
  },
  {
    name: 'set_owner',
    description: 'Change who is carrying a row. Owners are short names like Lead, Credit Hunter, Cash Controller, Tax File, Property DD.',
    parameters: { type: 'object', properties: { ref: { type: 'string' }, owner: { type: 'string' } }, required: ['ref', 'owner'] },
    immediate: false,
  },
  {
    name: 'add_note',
    description:
      'Append a working note to a row — something that happened, was said, or was confirmed. Notes are appended, never overwritten. Pick the row; the note text is taken from what the person actually said, so do not invent or paraphrase it.',
    parameters: { type: 'object', properties: { ref: { type: 'string' } }, required: ['ref'] },
    immediate: false,
  },
  {
    name: 'set_metric',
    description: 'Update a headline number. "fico" or "score" is the alert score, "min score" the lender minimum, "reserve" the protected closing reserve.',
    parameters: {
      type: 'object',
      properties: { metric: { type: 'string', description: 'fico | min score | reserve' }, value: { type: 'string' } },
      required: ['metric', 'value'],
    },
    immediate: false,
  },
  {
    name: 'raise_ticket',
    description:
      'Record a request that no other tool can carry out — something a person has to go and do. Use ONLY when nothing else fits. Never use it for a question about a row: that is show_row.',
    parameters: { type: 'object', properties: {}, required: [] },
    immediate: false,
  },
]

export interface Proposal {
  tool: string
  args: Record<string, string>
  /** The command the server will actually be given. */
  command: string
  /** Plain words for the confirm card. */
  says: string
  immediate: boolean
}

/*
 * What the model is told about the deal.
 *
 * Every field worth matching on, not just the title. Asked "has the deposit
 * gone out yet?", a list of titles cannot answer — "escrow deposit planned Sep
 * 1, receipt pending" lives in the evidence column, and matching on titles
 * alone is matching on the least informative field there is.
 */
function rowLines(vocab: Vocabulary): string {
  return vocab.rows
    .map((r) => {
      const bits = [`${r.ref}:`, r.label]
      if (r.status) bits.push(`(${r.status})`)
      if (r.context) bits.push(`| ${r.context}`)
      return bits.join(' ').slice(0, 300)
    })
    .join('\n')
}

const SYSTEM = (vocab: Vocabulary) => `You operate a land-purchase tracker. Choose exactly ONE tool.

Match the person's words against EVERY field below, not only the title — what
they are asking about is often named in the evidence rather than the heading.

These are the ONLY rows that exist. Never invent an id:
${rowLines(vocab)}

Rules:
- A question is never a ticket. Answer any question with show_row.
- Use raise_ticket only when no other tool can carry the request out.
- Never answer the question yourself. Pick the tool and the row.`

/** Turn a tool call into the command string the server's grammar will check. */
function toCommand(tool: string, args: Record<string, string>, said: string): Proposal | null {
  const ref = (args.ref ?? '').trim()
  switch (tool) {
    case 'show_row':
      return ref ? { tool, args, command: `tell me about ${ref}`, says: `Show ${ref}`, immediate: true } : null
    case 'set_status':
      return ref && args.status
        ? { tool, args, command: `${ref} status ${args.status}`, says: `Set ${ref} to ${args.status}`, immediate: false }
        : null
    case 'set_due':
      return ref && args.date
        ? { tool, args, command: `${ref} due ${args.date}`, says: `Move ${ref} to ${args.date}`, immediate: false }
        : null
    case 'set_owner':
      return ref && args.owner
        ? { tool, args, command: `${ref} owner ${args.owner}`, says: `Give ${ref} to ${args.owner}`, immediate: false }
        : null
    // The model picks the ROW. The words are the operator's own, always — asked
    // to note a deposit it once replied with the prompt's example verbatim.
    case 'add_note':
      return ref ? { tool, args, command: `note ${ref} ${said}`, says: `Add a note to ${ref}`, immediate: false } : null
    case 'set_metric':
      return args.metric && args.value
        ? { tool, args, command: `${args.metric} ${args.value}`, says: `Set ${args.metric} to ${args.value}`, immediate: false }
        : null
    case 'raise_ticket':
      return { tool, args, command: `ticket ${said}`, says: 'Raise a ticket', immediate: false }
    default:
      return null
  }
}

/** Ask the model which tool this is. Returns a proposal, or null. */
export async function interpret(vocab: Vocabulary, input: string): Promise<Proposal | null> {
  try {
    const res = await fetch(`${endpoint()}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: model(),
        messages: [
          { role: 'system', content: SYSTEM(vocab) },
          { role: 'user', content: input },
        ],
        tools: TOOLS.map((t) => ({ type: 'function', function: { name: t.name, description: t.description, parameters: t.parameters } })),
        stream: false,
        // The same sentence must not become a different edit on a second try.
        options: { temperature: 0 },
      }),
      signal: AbortSignal.timeout(60_000),
    })
    if (!res.ok) return null
    const body = (await res.json()) as {
      message?: { tool_calls?: Array<{ function?: { name?: string; arguments?: Record<string, string> } }> }
    }
    const call = body.message?.tool_calls?.[0]?.function
    if (!call?.name) return null
    return toCommand(call.name, call.arguments ?? {}, input)
  } catch {
    return null
  }
}
